import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileText, TrendingUp, Target, MessageSquare, Calendar, ExternalLink, Filter, Search, SortAsc } from "lucide-react";
import { menuItems } from "../config/menuItems";
import FavoriteButton from "../components/FavoriteButton";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function News({ user, onLogout }) {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("popularity");
  const [showArticleViewer, setShowArticleViewer] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const categories = [
    { value: "all", label: "All Articles" },
    { value: "sport-psychology", label: "Sport Psychology" },
    { value: "mental-performance", label: "Mental Performance" },
    { value: "wellness-trends", label: "Wellness Trends" },
    { value: "research", label: "Research" },
    { value: "university-research", label: "University Research" },
    { value: "aasp-updates", label: "AASP Updates" }
  ];

  const sortOptions = [
    { value: "popularity", label: "Most Popular" },
    { value: "date", label: "Newest First" },
    { value: "topic", label: "By Topic" }
  ];

  useEffect(() => {
    fetchNews();
  }, [selectedCategory, sortBy]);

  const fetchNews = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== "all") {
        params.append("category", selectedCategory);
      }
      if (searchTerm) {
        params.append("search", searchTerm);
      }
      params.append("sort_by", sortBy);

      const response = await axios.get(`${API}/news?${params.toString()}`);
      setArticles(response.data);
    } catch (error) {
      toast.error("Failed to load news articles");
    }
    setLoading(false);
  };

  const handleSearch = () => {
    fetchNews();
  };

  const handleArticleView = async (articleId) => {
    try {
      await axios.post(`${API}/news/${articleId}/view`);
    } catch (error) {
      console.error("Failed to track article view");
    }
  };

  const handleOpenArticle = (article) => {
    setSelectedArticle(article);
    setShowArticleViewer(true);
    handleArticleView(article.id);
  };

  const handleCloseArticleViewer = () => {
    setShowArticleViewer(false);
    setSelectedArticle(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-100">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-12">
        <div className="mb-12">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Sport Psychology News
          </h1>
          <p className="text-lg text-gray-600 mb-8">
            Stay updated with the latest insights from top sport psychology sources
          </p>

          {/* Search, Filter, and Sort */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Search */}
              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Search className="w-4 h-4 inline mr-1" />
                  Search Articles
                </label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder="Search by title, topic, or tags..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    className="flex-1"
                  />
                  <Button onClick={handleSearch} className="bg-teal-600 hover:bg-teal-700">
                    Search
                  </Button>
                </div>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <SortAsc className="w-4 h-4 inline mr-1" />
                  Sort By
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Filter className="w-4 h-4 inline mr-1" />
                  Category
                </label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
            </div>

          </div>

          {/* Results Info */}
          <div className="mb-4">
            <p className="text-sm text-gray-600">
              Showing <span className="font-semibold">{articles.length}</span> articles
              {sortBy === 'popularity' && ' (sorted by popularity)'}
              {sortBy === 'date' && ' (newest first)'}
              {sortBy === 'topic' && ' (sorted by topic)'}
            </p>
          </div>

          {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading news articles...</p>
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No articles found</h3>
            <p className="text-gray-600">Try adjusting your filters or check back later for new content.</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {articles.map((article) => (
              <Card key={article.id} className="hover:shadow-lg transition-shadow duration-200 bg-white">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {article.featured && (
                        <Badge className="bg-yellow-100 text-yellow-800 mb-2">Featured</Badge>
                      )}
                      <CardTitle className="text-lg leading-tight mb-2">
                        {article.title}
                      </CardTitle>
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                        <div className="flex items-center space-x-1">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(article.published_date)}</span>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {article.source?.toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent>
                  <CardDescription className="text-sm text-gray-600 mb-4 line-clamp-3">
                    {article.summary}
                  </CardDescription>

                  <div className="flex flex-wrap gap-1 mb-4">
                    {article.tags?.slice(0, 3).map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                    {article.sport_specific && (
                      <Badge className="bg-teal-100 text-teal-800 text-xs">
                        {article.sport_specific}
                      </Badge>
                    )}
                    {article.views > 0 && (
                      <Badge className="bg-purple-100 text-purple-800 text-xs">
                        👁️ {article.views} views
                      </Badge>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {article.author && (
                        <p className="text-sm text-gray-500">By {article.author}</p>
                      )}
                      <FavoriteButton
                        itemType="article"
                        itemId={article.id}
                        itemTitle={article.title}
                        itemSource="news"
                        itemData={{
                          author: article.author,
                          source: article.source,
                          published_date: article.published_date,
                          tags: article.tags
                        }}
                        size="sm"
                      />
                    </div>
                    {article.url && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-teal-600 border-teal-600 hover:bg-teal-50"
                        onClick={() => handleOpenArticle(article)}
                      >
                        Read Article
                        <FileText className="w-3 h-3 ml-1" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
        </div>

        {/* Article Viewer Modal */}
        {showArticleViewer && selectedArticle && (
          <div 
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={handleCloseArticleViewer}
          >
            <div 
              className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-6 text-white flex-shrink-0">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-3">{selectedArticle.title}</h3>
                    <div className="flex items-center gap-4 text-teal-100 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(selectedArticle.published_date || selectedArticle.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        {selectedArticle.source}
                      </span>
                      {selectedArticle.views && (
                        <span className="flex items-center gap-1">
                          <TrendingUp className="w-4 h-4" />
                          {selectedArticle.views} views
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={handleCloseArticleViewer}
                    className="ml-4 p-2 hover:bg-white/20 rounded-full transition-colors flex-shrink-0"
                    aria-label="Close article viewer"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Article Content */}
              <div className="flex-1 overflow-y-auto">
                <div className="p-8">
                  {/* Summary */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 text-lg mb-3">Summary</h4>
                    <p className="text-gray-700 leading-relaxed text-base">{selectedArticle.summary}</p>
                  </div>

                  {/* Category & Tags */}
                  {selectedArticle.category && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 text-sm mb-2">Category</h4>
                      <Badge className="bg-teal-100 text-teal-800 border-teal-300">
                        {selectedArticle.category}
                      </Badge>
                    </div>
                  )}

                  {selectedArticle.tags && selectedArticle.tags.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-semibold text-gray-900 text-sm mb-2">Topics</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedArticle.tags.map((tag, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* External Article Embed */}
                  {selectedArticle.url && (
                    <div className="mt-8">
                      <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6">
                        <h4 className="font-semibold text-gray-900 text-lg mb-4 flex items-center gap-2">
                          <FileText className="w-5 h-5" />
                          Full Article
                        </h4>
                        
                        <div className="bg-white rounded-lg border border-gray-300 overflow-hidden" style={{ height: '600px' }}>
                          <iframe
                            src={selectedArticle.url}
                            title={selectedArticle.title}
                            className="w-full h-full"
                            frameBorder="0"
                            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                          />
                        </div>
                        
                        <div className="mt-4 flex items-center justify-between">
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Source:</span> {selectedArticle.source}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Actions */}
              <div className="p-6 bg-gray-50 border-t border-gray-200 flex-shrink-0">
                <div className="flex gap-3">
                  <Button
                    onClick={() => window.open(selectedArticle.url, '_blank')}
                    className="bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Open in New Tab
                  </Button>
                  <div onClick={(e) => e.stopPropagation()}>
                    <FavoriteButton
                      itemType="article"
                      itemId={selectedArticle.id}
                      itemTitle={selectedArticle.title}
                      itemSource="news"
                      itemData={{
                        summary: selectedArticle.summary,
                        source: selectedArticle.source,
                        url: selectedArticle.url,
                        published_date: selectedArticle.published_date,
                        category: selectedArticle.category
                      }}
                      size="md"
                    />
                  </div>
                  <Button
                    onClick={handleCloseArticleViewer}
                    variant="outline"
                  >
                    Close
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}